import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { normalizedPreflightTenantId, preflightKeys } from "@/lib/queries";
import { updateReportMarket } from "../api/preflight.api";
import {
  bumpMarketReportCount,
  invalidateMarketAggregates,
} from "../lib/market-cache";
import { patchReportInCaches } from "../lib/report-cache";
import type {
  PreflightMarket,
  PreflightReportSummary,
} from "../types/preflight.types";

export function useUpdateReportMarket(
  report: Pick<PreflightReportSummary, "id" | "markets">,
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const tenantId = normalizedPreflightTenantId(activeTenantId);

  return useMutation({
    mutationFn: (nextMarkets: PreflightMarket[]) =>
      updateReportMarket(
        report.id,
        nextMarkets.map((market) => market.id),
      ),
    onMutate: (nextMarkets) => {
      const previousMarkets = report.markets;
      patchReportInCaches(queryClient, report.id, { markets: nextMarkets });
      const previousIds = new Set(previousMarkets.map((market) => market.id));
      const nextIds = new Set(nextMarkets.map((market) => market.id));
      for (const market of previousMarkets) {
        if (!nextIds.has(market.id)) {
          bumpMarketReportCount(queryClient, tenantId, market.id, -1);
        }
      }
      for (const market of nextMarkets) {
        if (!previousIds.has(market.id)) {
          bumpMarketReportCount(queryClient, tenantId, market.id, 1);
        }
      }
      return { previousMarkets };
    },
    onError: (_error, nextMarkets, context) => {
      if (context) {
        patchReportInCaches(queryClient, report.id, {
          markets: context.previousMarkets,
        });
        const previousIds = new Set(
          context.previousMarkets.map((market) => market.id),
        );
        const nextIds = new Set(nextMarkets.map((market) => market.id));
        for (const market of nextMarkets) {
          if (!previousIds.has(market.id)) {
            bumpMarketReportCount(queryClient, tenantId, market.id, -1);
          }
        }
        for (const market of context.previousMarkets) {
          if (!nextIds.has(market.id)) {
            bumpMarketReportCount(queryClient, tenantId, market.id, 1);
          }
        }
      }
      toast.error(t("preflight.toast.changeMarketError"));
    },
    onSuccess: () => {
      invalidateMarketAggregates(queryClient, tenantId);
      void queryClient.invalidateQueries({ queryKey: preflightKeys.lists() });
      toast.success(t("preflight.toast.marketChanged"));
    },
    meta: { skipGlobalError: true },
  });
}
